import { createRoot } from 'react-dom/client'

import { ThemeProvider } from '@emotion/react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { tokens } from '@ugleelab/design-system/tokens'

import { setupBridgeMock } from '@onl-web/shared/bridge'
import { WebViewGuard } from '@onl-web/shared/guard'

import App from './App'
import GlobalErrorBoundary from './components/GlobalErrorBoundary'
import { reportError } from './components/GlobalErrorBoundary/reportError'
import { ToastProvider } from './components/Toast'
import { APP_REDIRECT_URL } from './constants'
import { getMockMode } from './lib/devMode'
import { initSentry } from './lib/sentry'
import GlobalStyles from './styles/GlobalStyles'

import '@ugleelab/design-system/styles.css'

initSentry()

if (getMockMode()) {
  setupBridgeMock()
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // 결제 webview 정책: 매 mount 시 fresh fetch 보장 (옛 데이터 일시 표시 방지).
      // staleTime 0 — mount 즉시 stale 처리해 refetch 트리거.
      // gcTime 0 — unmount 시 캐시 즉시 폐기해 다음 mount 시 캐시 hit 자체 없음.
      staleTime: 0,
      gcTime: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      reportError(error, { source: 'react-query', level: 'error' })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      reportError(error, { source: 'react-query', level: 'error' })
    },
  }),
})

async function enableMocking() {
  if (!getMockMode()) return
  const { worker } = await import('./mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

enableMocking().then(() => {
  createRoot(document.getElementById('root')!).render(
    <WebViewGuard fallbackUrl={APP_REDIRECT_URL}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={tokens}>
          <GlobalStyles />
          <ToastProvider>
            <GlobalErrorBoundary>
              <App />
            </GlobalErrorBoundary>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </WebViewGuard>
  )
})
